import React, { useState, useEffect } from 'react';
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from 'react-icons/ai';
import { BsTwitter, BsYoutube, BsInstagram } from 'react-icons/bs';
import { FaFacebookF } from 'react-icons/fa';
import { SiLinkedin } from 'react-icons/si';
import { useSelector } from 'react-redux';
import 'react-phone-input-2/lib/style.css';
import validator from 'validator';
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle, Clock, ChevronRight, X, ExternalLink } from 'lucide-react';
import { createInquiry } from '../../../api/action/inquiry';
import { useTheme } from '../../../context/ThemeContext.jsx';
import { buildInquiryNotificationFields } from '../../../template/TemplateContactUtils.jsx';

export const DOMAIN_TEXT = {
  FLEX_OFFICE: {
    heading: "Get in touch",
    headingAccent: "touch",
    description: "Have questions about premium workspaces? We're here to assist you.",
    accent: "#0078d4",
    accentHover: "#106ebe",
    accentLight: "#f0f6ff",
    accentBorder: "#c7e0f4",
    tag: "Workspace Support",
  },
  ASSESSMENT: {
    heading: "Start your assessment",
    headingAccent: "assessment",
    description: "Need support with exams or learning? Our team is ready to help.",
    accent: "#5c2d91",
    accentHover: "#4b1f7a",
    accentLight: "#f4f0f9",
    accentBorder: "#d9c9ed",
    tag: "Learning Support",
  },
  RETAIL: {
    heading: "Connect with Avikar",
    headingAccent: "Avikar",
    description: "We'd love to hear your thoughts and inquiries about our products.",
    accent: "#d47500",
    accentHover: "#b86400",
    accentLight: "#fff9f0",
    accentBorder: "#f7d9aa",
    tag: "Retail Inquiries",
  },
  EDUCATION: {
    heading: "Get in touch",
    headingAccent: "touch",
    description: "Have questions about admissions or courses? We're here to guide you.",
    accent: "#5c2d91",
    accentHover: "#4b1f7a",
    accentLight: "#f4f0f9",
    accentBorder: "#d9c9ed",
    tag: "Academic Support",
  },
};

export const DEFAULT_CONTACT_CONFIG = {
  heading: "Get in touch",
  headingAccent: "touch",
  description: "Have questions or need support? We're here to help you.",
  accent: "#0078d4",
  accentHover: "#106ebe",
  accentLight: "#f0f6ff",
  accentBorder: "#c7e0f4",
  tag: "General Support",
};

const formatPhone = (phone) => {
  if (!phone) return "";
  let cleaned = phone.replace(/\D/g, "");
  cleaned = cleaned.replace(/^0+/, "");

  // US number: starts with "1" and is 11 digits
  if (cleaned.startsWith("1") && cleaned.length === 11) {
    const num = cleaned.substring(1);
    return `+1 ${num.slice(0, 3)} ${num.slice(3, 6)} ${num.slice(6)}`;
  }
  // Indian number: starts with "91" and is 12 digits
  if (cleaned.startsWith("91") && cleaned.length === 12) {
    const num = cleaned.substring(2);
    return `+91 ${num.slice(0, 5)} ${num.slice(5)}`;
  }
  // Plain 10-digit: assume US
  if (cleaned.length === 10) {
    return `+1 ${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }
  return `+${cleaned}`;
};

const formatSocialUrl = (url) => {
  if (!url || url === "#") return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.includes(".com") || url.includes(".org") || url.includes(".net")) return `https://${url}`;
  return null;
};

// Helper function to find social media account by name
const findSocialMedia = (company, name) => {
  if (!company?.socialMediaAccounts || !Array.isArray(company.socialMediaAccounts)) {
    return null;
  }
  const account = company.socialMediaAccounts.find(
    s => s?.name?.toLowerCase() === name.toLowerCase() ||
      s?.name?.toLowerCase() === name.toLowerCase().replace(' ', '')
  );
  return account?.value || null;
};

// Helper function to get US state abbreviation
const getStateAbbreviation = (stateName) => {
  const stateMap = {
    'Alabama': 'AL',
    'Alaska': 'AK',
    'Arizona': 'AZ',
    'Arkansas': 'AR',
    'California': 'CA',
    'Colorado': 'CO',
    'Connecticut': 'CT',
    'Delaware': 'DE',
    'Florida': 'FL',
    'Georgia': 'GA',
    'Hawaii': 'HI',
    'Idaho': 'ID',
    'Illinois': 'IL',
    'Indiana': 'IN',
    'Iowa': 'IA',
    'Kansas': 'KS',
    'Kentucky': 'KY',
    'Louisiana': 'LA',
    'Maine': 'ME',
    'Maryland': 'MD',
    'Massachusetts': 'MA',
    'Michigan': 'MI',
    'Minnesota': 'MN',
    'Mississippi': 'MS',
    'Missouri': 'MO',
    'Montana': 'MT',
    'Nebraska': 'NE',
    'Nevada': 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    'Ohio': 'OH',
    'Oklahoma': 'OK',
    'Oregon': 'OR',
    'Pennsylvania': 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    'Tennessee': 'TN',
    'Texas': 'TX',
    'Utah': 'UT',
    'Vermont': 'VT',
    'Virginia': 'VA',
    'Washington': 'WA',
    'West Virginia': 'WV',
    'Wisconsin': 'WI',
    'Wyoming': 'WY',
    'District of Columbia': 'DC',
    'Puerto Rico': 'PR',
    'Guam': 'GU',
    'American Samoa': 'AS',
    'U.S. Virgin Islands': 'VI',
    'Northern Mariana Islands': 'MP'
  };

  // If it's already an abbreviation (2 letters), return as is
  if (stateName && stateName.length === 2 && stateName === stateName.toUpperCase()) {
    return stateName;
  }

  return stateMap[stateName] || stateName;
};

// Updated formatAddress function to match required format:
// Street 1
// Street 2 (if exists)
// City , State - Zip Code
const formatAddress = (address) => {
  if (!address) return "";

  const parts = [];

  // Street address - line 1
  if (address.streetName1) {
    parts.push(address.streetName1);
  }

  // Street address - line 2 (if exists)
  if (address.streetName2) {
    parts.push(address.streetName2);
  }

  // City, State - Zip Code line
  const cityStateZip = [];
  if (address.city) {
    cityStateZip.push(address.city);
  }

  // Format state with abbreviation
  if (address.state) {
    const stateAbbr = getStateAbbreviation(address.state);
    cityStateZip.push(stateAbbr);
  }

  // Add zipcode with proper formatting
  if (address.zipcode) {
    // If cityStateZip has content, add zipcode with a dash prefix
    if (cityStateZip.length > 0) {
      cityStateZip[cityStateZip.length - 1] = cityStateZip[cityStateZip.length - 1] + " - " + address.zipcode;
    } else {
      cityStateZip.push(address.zipcode);
    }
  }

  // Join the city, state, zip part
  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(", "));
  }

  // Return with line breaks between parts
  return parts.join("\n");
};

// Social Media Modal - Only shows accounts that exist
const SocialMediaModal = ({ company, accent, accentLight, accentBorder, onClose }) => {
  // Define all possible social media platforms with their configurations
  const allSocialPlatforms = [
    {
      icon: BsTwitter,
      label: "Twitter / X",
      key: "twitter",
      color: "#1DA1F2",
      bg: "#e8f5fe",
      border: "#bae0fd",
      desc: "Follow us for updates",
      nameMatches: ["twitter", "x"]
    },
    {
      icon: SiLinkedin,
      label: "LinkedIn",
      key: "linkedin",
      color: "#0A66C2",
      bg: "#e8f0fb",
      border: "#b9d0f5",
      desc: "Connect professionally",
      nameMatches: ["linkedin", "iinkedin"]
    },
    {
      icon: BsYoutube,
      label: "YouTube",
      key: "youtube",
      color: "#FF0000",
      bg: "#fee8e8",
      border: "#fdb8b8",
      desc: "Watch our latest content",
      nameMatches: ["youtube"]
    },
    {
      icon: FaFacebookF,
      label: "Facebook",
      key: "facebook",
      color: "#1877F2",
      bg: "#e8f0fe",
      border: "#b8d0fc",
      desc: "Like our page",
      nameMatches: ["facebook"]
    },
    {
      icon: BsInstagram,
      label: "Instagram",
      key: "instagram",
      color: "#E4405F",
      bg: "#fef0f3",
      border: "#fdced8",
      desc: "Follow our journey",
      nameMatches: ["instagram", "ig"]
    }
  ];

  // Filter to only show platforms that have valid URLs from the company
  const socialLinks = allSocialPlatforms
    .map(platform => {
      // Try to find the URL using any of the matching names
      let url = null;
      for (const matchName of platform.nameMatches) {
        const foundUrl = findSocialMedia(company, matchName);
        if (foundUrl) {
          url = formatSocialUrl(foundUrl);
          break;
        }
      }
      return {
        ...platform,
        url
      };
    })
    .filter(platform => platform.url && platform.url !== "#"); // Only keep platforms with valid URLs

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => { setWindowWidth(window.innerWidth); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 640;

  // Don't render modal if there are no social links
  if (socialLinks.length === 0) {
    return null;
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 8, width: "100%", maxWidth: isMobile ? 340 : 400,
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden",
          animation: "slideUp 0.25s cubic-bezier(0.34,1.4,0.64,1)",
          margin: 16,
        }}
      >
        <div style={{
          padding: isMobile ? "14px" : "16px 20px", borderBottom: "1px solid #edebe9",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: accentLight,
        }}>
          <div>
            <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: "#201f1e" }}>Connect with us</div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: "#605e5c", marginTop: 2 }}>Follow us on social media</div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, borderRadius: 4, border: `1px solid ${accentBorder}`,
              background: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", color: "#605e5c", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#f3f2f1"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
          >
            <X size={isMobile ? 13 : 14} />
          </button>
        </div>

        <div style={{ padding: isMobile ? "8px 4px" : "12px 8px" }}>
          {socialLinks.map((s, i) => {
            const Icon = s.icon;
            return (
              <a
                key={i}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex", alignItems: "center", gap: isMobile ? 10 : 14,
                  padding: isMobile ? "10px 12px" : "12px 14px",
                  borderRadius: 6, textDecoration: "none", transition: "background 0.12s",
                  cursor: "pointer",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#f3f2f1"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{
                  width: isMobile ? 36 : 42, height: isMobile ? 36 : 42, borderRadius: 8, flexShrink: 0,
                  background: s.bg, border: `1.5px solid ${s.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: s.color, fontSize: isMobile ? 16 : 18,
                }}>
                  <Icon />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#201f1e" }}>{s.label}</div>
                  <div style={{ fontSize: isMobile ? 11 : 12, color: "#a19f9d" }}>{s.desc}</div>
                </div>
                <ExternalLink size={isMobile ? 12 : 13} color="#a19f9d" />
              </a>
            );
          })}
        </div>

        <div style={{ padding: isMobile ? "10px 14px 14px" : "10px 20px 16px", borderTop: "1px solid #edebe9", textAlign: "center" }}>
          <span style={{ fontSize: isMobile ? 11 : 12, color: "#a19f9d" }}>Stay updated with our latest news and announcements</span>
        </div>
      </div>
    </div>
  );
};

const KrumagoContact = () => {
  const { activeMode, isFlexOfficeScopeActive } = useTheme() || {};
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phoneNumber: '', phoneDisplay: '', query: '', contactPersonList: []
  });
  const [errors, setErrors] = useState({ firstName: '', lastName: '', email: '', phoneNumber: '', query: '' });
  const [alert, setAlert] = useState({ message: "", type: "", show: false });
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const company = useSelector((state) => state?.companyReducer?.company) || {};

  // Check if company has any social media accounts
  const hasSocialMediaAccounts = () => {
    if (!company?.socialMediaAccounts || !Array.isArray(company.socialMediaAccounts)) {
      return false;
    }
    return company.socialMediaAccounts.some(account => account?.value && account.value !== "#");
  };

  useEffect(() => {
    const handleResize = () => { setWindowWidth(window.innerWidth); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatPhoneInput = (digits) => {
    if (!digits) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  };

  const handlePhoneChange = (value) => {
    // Strip everything except digits
    let d = value.replace(/\D/g, '');
    if (d.length > 10) d = d.slice(0, 10);
    // Store raw digits for validation/submission, display formatted
    setFormData({ ...formData, phoneNumber: d, phoneDisplay: formatPhoneInput(d) });
    setErrors({ ...errors, phoneNumber: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { firstName: '', lastName: '', email: '', phoneNumber: '', query: '' };
    if (!formData.firstName.trim() || formData.firstName.trim().length < 2) { newErrors.firstName = 'First name must be at least 2 characters'; valid = false; }
    if (!formData.lastName.trim() || formData.lastName.trim().length < 2) { newErrors.lastName = 'Last name must be at least 2 characters'; valid = false; }
    if (!formData.email.trim() || !validator.isEmail(formData.email)) { newErrors.email = 'Please enter a valid email address'; valid = false; }
    if (!formData.phoneNumber || formData.phoneNumber.replace(/\D/g, '').length !== 10) { newErrors.phoneNumber = 'Please enter a 10-digit phone number'; valid = false; }
    if (!formData.query.trim()) { newErrors.query = 'Message is required'; valid = false; }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    const loc = company?.operatingLocation?.code;
    const countryCode = loc === "IND" ? "+91" : loc === "US" ? "+1" : "";
    const inquiryData = {
      user: {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber,
        countryCode,
        contactPersonList: []
      },
      query: formData.query.trim(),
      company: {
        primaryId: company?.primaryId,
        name: company?.name,
        url: company?.url,
        domain: { type: company?.domain?.type || "INSTITUTION" }
      },
      ...buildInquiryNotificationFields(company)
    };
    try {
      const result = await createInquiry(inquiryData);
      localStorage.setItem("inquiryData", JSON.stringify(result));
      setAlert({ message: "Your enquiry has been sent successfully! We'll get back to you soon.", type: "success", show: true });
      setFormData({ firstName: '', lastName: '', email: '', phoneNumber: '', phoneDisplay: '', query: '', contactPersonList: [] });
    } catch {
      setAlert({ message: "Error sending enquiry. Please check your connection and try again.", type: "error", show: true });
    } finally {
      setLoading(false);
    }
  };

  const schedule = company?.workingSchedule || {};
  const openHours = schedule.openHours || "N/A";
  const closeHours = schedule.closeHours || "N/A";

  //  12-hour format function
  const formatTime12Hour = (time) => {
    if (!time || time === "N/A") return null;

    let [hour, minute] = time.split(":").map(Number);

    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;

    return {
      hour: hour.toString().padStart(2, "0"),
      minute: (minute || 0).toString().padStart(2, "0"),
      period
    };
  };

  const openTime = formatTime12Hour(openHours);
  const closeTime = formatTime12Hour(closeHours);

  // Check if working hours are valid (not N/A and both times exist)
  const hasValidWorkingHours = openTime && closeTime && openHours !== "N/A" && closeHours !== "N/A";

  useEffect(() => {
    if (alert.show) {
      const t = setTimeout(() => setAlert({ ...alert, show: false }), 5000);
      return () => clearTimeout(t);
    }
  }, [alert]);

  const detectCountryCode = (phone) => {
    if (!phone) return "+1";
    const cleaned = phone.replace(/\D/g, "").replace(/^0+/, "");
    if (cleaned.startsWith("1") && cleaned.length === 11) return "+1";
    if (cleaned.startsWith("91") && cleaned.length === 12) return "+91";
    return "+1";
  };
  const countryCode = detectCountryCode(company?.contactPerson?.phoneNumber);
  const domainType = company?.domain?.type || "UNKNOWN";
  const cfg = DOMAIN_TEXT[domainType] || DEFAULT_CONTACT_CONFIG;
  const { heading, headingAccent, description, accent, accentLight, accentBorder, tag } = cfg;
  const isFlexDark = isFlexOfficeScopeActive && activeMode === "dark";
  const pageBg = isFlexDark ? "var(--flex-bg)" : "#faf9f8";
  const panelBg = isFlexDark ? "var(--flex-surface)" : "#fff";
  const inputBg = isFlexDark ? "#111c2d" : "#ffffff";
  const inputErrorBg = isFlexDark ? "rgba(248,113,113,0.12)" : "#fdf3f4";
  const borderColor = isFlexDark ? "var(--flex-border)" : "#edebe9";
  const inputBorderColor = isFlexDark ? "var(--flex-border-strong)" : "#d1d1d1";
  const titleColor = isFlexDark ? "var(--flex-text)" : "#201f1e";
  const bodyColor = isFlexDark ? "var(--flex-text-muted)" : "#605e5c";
  const subtleColor = isFlexDark ? "var(--flex-text-subtle)" : "#a19f9d";
  const themedAccentLight = isFlexDark ? "var(--flex-primary-soft)" : accentLight;
  const themedAccentBorder = isFlexDark ? "rgba(96, 165, 250, 0.32)" : accentBorder;

  const headingParts = heading.split(headingAccent);

  // Contact details with updated address formatting - conditionally include Hours
  const contactDetails = [
    {
      icon: Mail,
      label: "Email",
      value: company?.contactPerson?.email || "inquiry@avpstech.com",
      note: null,
    },
    {
      icon: Phone,
      label: "Phone",
      value: formatPhone(company?.contactPerson?.phoneNumber) || "+1 678 322 7153",
      note: null,
    },
    {
      icon: MapPin,
      label: "Office",
      value: formatAddress(company?.address) || "6110 McFarland Station Drive\n# 601\nAlpharetta, GA - 30004",
      note: null,
    },
  ];

  // Add Hours section only if working hours are valid
  if (hasValidWorkingHours) {
    contactDetails.push({
      icon: Clock,
      label: "Hours",
      value: `${openTime.hour}:${openTime.minute} ${openTime.period} - ${closeTime.hour}:${closeTime.minute} ${closeTime.period}`,
      note: null,
    });
  }

  const SF = "'Segoe UI Variable', 'Segoe UI', system-ui, -apple-system, sans-serif";

  const inputBase = {
    width: "100%", boxSizing: "border-box",
    padding: "9px 12px", fontSize: 14, fontFamily: SF,
    color: titleColor, background: inputBg,
    border: `1px solid ${inputBorderColor}`, borderRadius: 4, outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
  };

  const getInputStyle = (field) => {
    const err = !!errors[field];
    const focused = focusedField === field;
    const filled = !!formData[field];
    return {
      ...inputBase,
      borderColor: err ? "#ef4444" : focused ? accent : filled ? (isFlexDark ? "var(--flex-border-strong)" : "#b3b0ad") : inputBorderColor,
      boxShadow: focused ? `0 0 0 1px ${accent}` : err ? "0 0 0 1px #a4262c" : "none",
      background: err ? inputErrorBg : inputBg,
    };
  };

  const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: titleColor, marginBottom: 5, fontFamily: SF };
  const errorStyle = { display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#a4262c", marginTop: 4, fontFamily: SF };

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  // Function to render address with line breaks
  const renderAddress = (addressValue) => {
    if (!addressValue) return null;

    // Split by newline and render each line
    const lines = addressValue.split('\n');

    return (
      <div>
        {lines.map((line, index) => (
          <div key={index} style={{ marginBottom: index < lines.length - 1 ? 2 : 0 }}>
            {line}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      id="contact-section"
      style={{ background: pageBg, minHeight: "100vh", padding: isMobile ? "96px 16px 40px" : "112px 24px 64px", fontFamily: SF, position: "relative" }}
    >
      <style>{`
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(24px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }

        .kc-anim  { animation: fadeUp 0.4s ease both; }
        .kc-anim2 { animation: fadeUp 0.4s ease 0.08s both; }
        .kc-anim3 { animation: fadeUp 0.4s ease 0.16s both; }

        .kc-contact-row { display:flex; align-items:flex-start; gap:14px; padding:14px 16px; border-radius:4px; transition:background 0.12s; cursor:default; }
        .kc-contact-row:hover { background:${isFlexDark ? "var(--flex-surface-soft)" : "#f3f2f1"}; }

        @media (max-width: 640px) {
          .kc-contact-row { padding:12px 14px; gap:12px; }
        }

        .kc-btn {
          display:inline-flex; align-items:center; gap:8px;
          padding:9px 20px; font-size:14px; font-weight:600;
          color:#fff; border:none; border-radius:4px; cursor:pointer;
          font-family: 'Segoe UI Variable','Segoe UI',system-ui,sans-serif;
          transition: background 0.15s, box-shadow 0.15s, transform 0.1s;
          white-space: nowrap; position: relative; overflow: hidden;
        }
        
        @media (max-width: 640px) {
          .kc-btn { padding:9px 16px; font-size:13px; }
        }
        
        .kc-btn:hover:not(:disabled) { box-shadow: 0 4px 14px rgba(0,0,0,0.16); transform:translateY(-1px); }
        .kc-btn:active:not(:disabled) { transform:translateY(0); box-shadow:none; }
        .kc-btn::after {
          content:''; position:absolute; inset:0;
          background:linear-gradient(120deg,transparent 30%,rgba(255,255,255,0.15) 50%,transparent 70%);
          background-size:200% 100%; opacity:0; transition:opacity 0.2s;
        }
        .kc-btn:hover::after { opacity:1; animation:shimmer 0.55s ease; }
        .kc-btn:disabled { cursor:not-allowed; }

        .kc-view-all { display:flex; align-items:center; justify-content:space-between; padding:12px 20px; cursor:pointer; transition:background 0.12s; }
        .kc-view-all:hover { background:${isFlexDark ? "var(--flex-surface-soft)" : "#f3f2f1"}; }
        
        @media (max-width: 640px) {
          .kc-view-all { padding:10px 16px; }
        }
      `}</style>

      {showSocialModal && hasSocialMediaAccounts() && (
        <SocialMediaModal
          company={company}
          accent={accent}
          accentLight={accentLight}
          accentBorder={accentBorder}
          onClose={() => setShowSocialModal(false)}
        />
      )}

      <div style={{ maxWidth: 1080, margin: "0 auto" }}>

        {/* Page header */}
        <div className="kc-anim" style={{ marginBottom: isMobile ? 32 : 44 }}>
          <span style={{
            display: "inline-flex", alignItems: "center",
            fontSize: isMobile ? 11 : 12, fontWeight: 600, color: accent,
            background: themedAccentLight, border: `1px solid ${themedAccentBorder}`,
            padding: isMobile ? "3px 8px" : "3px 10px", borderRadius: 2, marginBottom: isMobile ? 12 : 14,
          }}>
            {tag}
          </span>

          <h1
            id="contact-heading"
            style={{ fontSize: isMobile ? "24px" : isTablet ? "30px" : "clamp(26px,3.5vw,40px)", fontWeight: 700, color: titleColor, margin: "0 0 10px", lineHeight: 1.15, letterSpacing: "-0.4px" }}
          >
            {headingParts[0]}<span style={{ color: accent }}>{headingAccent}</span>{headingParts[1]}
          </h1>

          <p
            id="contact-description"
            style={{ fontSize: isMobile ? 14 : 16, color: bodyColor, maxWidth: 480, margin: 0, lineHeight: 1.6 }}
          >
            {description}
          </p>
        </div>

        {/* Layout */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : isTablet ? "280px 1fr" : "320px 1fr",
          gap: isMobile ? 24 : 28,
          alignItems: "start"
        }}>

          {/* Left info panel */}
          <div className="kc-anim2" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <div style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, overflow: "hidden", boxShadow: isFlexDark ? "var(--flex-shadow)" : "0 1px 6px rgba(0,0,0,0.06)" }}>
              <div style={{ height: 3, background: accent }} />

              <div style={{ padding: isMobile ? "16px 16px 10px" : "20px 20px 12px" }}>
                <p style={{ fontSize: isMobile ? 13 : 13.5, fontWeight: 600, color: titleColor, margin: "0 0 3px" }}>Contact information</p>
                <p style={{ fontSize: isMobile ? 12 : 13, color: subtleColor, margin: 0, lineHeight: 1.5 }}>Reach out through any of the channels below.</p>
              </div>

              <div style={{ height: 1, background: borderColor }} />

              <div style={{ padding: isMobile ? "4px 0" : "6px 4px" }}>
                {contactDetails.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <React.Fragment key={i}>
                      <div className="kc-contact-row">
                        <div style={{
                          width: isMobile ? 32 : 34,
                          height: isMobile ? 32 : 34,
                          borderRadius: 4, flexShrink: 0,
                          background: themedAccentLight,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: accent,
                          marginTop: 1
                        }}>
                          <Icon size={isMobile ? 14 : 15} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: isMobile ? 10 : 11, fontWeight: 600, color: subtleColor, marginBottom: 2 }}>{item.label}</div>
                          <div style={{ fontSize: isMobile ? 13 : 13.5, fontWeight: 500, color: titleColor, marginBottom: item.note ? 1 : 0, wordBreak: "break-word" }}>
                            {item.label === "Office" ? renderAddress(item.value) : item.value}
                          </div>
                          {item.note && <div style={{ fontSize: isMobile ? 11 : 12, color: subtleColor }}>{item.note}</div>}
                        </div>
                      </div>
                      {i < contactDetails.length - 1 && <div style={{ height: 1, background: borderColor, margin: "0 16px" }} />}
                    </React.Fragment>
                  );
                })}
              </div>

              <div style={{ height: 1, background: borderColor }} />

              {/* View all contact options — only show if company has social media accounts */}
              {hasSocialMediaAccounts() && (
                <div
                  className="kc-view-all"
                  onClick={() => setShowSocialModal(true)}
                >
                  <span style={{ fontSize: isMobile ? 12 : 12.5, color: bodyColor }}>View all contact options</span>
                  <ChevronRight size={isMobile ? 13 : 14} color="#a19f9d" />
                </div>
              )}
            </div>

            {/* Callout */}
            <div style={{
              padding: isMobile ? "12px 14px" : "13px 15px",
              background: themedAccentLight,
              border: `1px solid ${themedAccentBorder}`,
              borderRadius: 4,
              display: "flex",
              alignItems: "flex-start",
              gap: 10
            }}>
              <CheckCircle2 size={isMobile ? 14 : 15} color={accent} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: titleColor, marginBottom: 2 }}>We typically respond within 24 to 48 hours</div>
                <div style={{ fontSize: isMobile ? 11 : 12, color: bodyColor, lineHeight: 1.5 }}>For urgent matters, call us directly during business hours.</div>
              </div>
            </div>
          </div>

          {/* Right form panel */}
          <div className="kc-anim3" style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, boxShadow: isFlexDark ? "var(--flex-shadow)" : "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>

            <div style={{
              padding: isMobile ? "16px" : "18px 24px 16px",
              borderBottom: `1px solid ${borderColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div>
                <h2 style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: titleColor, margin: "0 0 2px" }}>Send a message</h2>
                <p style={{ fontSize: isMobile ? 12 : 12.5, color: subtleColor, margin: 0 }}>Fill in the details and we'll be in touch shortly.</p>
              </div>
              <div style={{
                width: isMobile ? 28 : 32,
                height: isMobile ? 28 : 32,
                borderRadius: 4,
                background: themedAccentLight,
                border: `1px solid ${themedAccentBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accent
              }}>
                <Send size={isMobile ? 13 : 14} />
              </div>
            </div>

            <div style={{ padding: isMobile ? "18px 16px 22px" : "22px 24px 26px" }}>

              {/* Alert */}
              {alert.show && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: isMobile ? "10px 12px" : "11px 13px", borderRadius: 4, marginBottom: 18,
                  background: alert.type === 'success' ? "#dff6dd" : "#fde7e9",
                  border: `1px solid ${alert.type === 'success' ? "#a9d3a8" : "#f1aaad"}`,
                  color: alert.type === 'success' ? "#1e5c1a" : "#750b1c",
                  animation: "fadeUp 0.25s ease",
                }}>
                  {alert.type === 'success'
                    ? <AiOutlineCheckCircle style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />
                    : <AiOutlineCloseCircle style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />
                  }
                  <span style={{ fontSize: isMobile ? 13 : 13.5, lineHeight: 1.5 }}>{alert.message}</span>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>

                {/* Name row */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: isMobile ? 12 : 14
                }}>
                  {[{ id: "firstName", label: "First name", ph: "" }, { id: "lastName", label: "Last name", ph: "" }].map(({ id, label, ph }) => (
                    <div key={id}>
                      <label htmlFor={id} style={labelStyle}>{label} <span style={{ color: "#a4262c", fontWeight: 400 }}>*</span></label>
                      <input id={id} name={id} type="text" value={formData[id]} onChange={handleChange}
                        onFocus={() => setFocusedField(id)} onBlur={() => setFocusedField('')}
                        placeholder={ph} style={getInputStyle(id)} autoComplete="off" />
                      {errors[id] && <div style={errorStyle}><AlertCircle size={11} />{errors[id]}</div>}
                    </div>
                  ))}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" style={labelStyle}>Email address <span style={{ color: "#a4262c", fontWeight: 400 }}>*</span></label>
                  <input id="email" name="email" type="email" value={formData.email} onChange={handleChange}
                    onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField('')}
                    placeholder="" style={getInputStyle('email')} autoComplete="off" />
                  {errors.email && <div style={errorStyle}><AlertCircle size={11} />{errors.email}</div>}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phoneNumber" style={labelStyle}>Phone number <span style={{ color: "#a4262c", fontWeight: 400 }}>*</span></label>
                  <div style={{ position: "relative" }}>
                    {countryCode && (
                      <span style={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: isMobile ? 13 : 13.5,
                        fontWeight: 600,
                        color: bodyColor,
                        borderRight: `1px solid ${inputBorderColor}`,
                        paddingRight: 10,
                        lineHeight: 1,
                        pointerEvents: "none",
                        zIndex: 1
                      }}>
                        {countryCode}
                      </span>
                    )}
                    <input id="phoneNumber" name="phoneNumber" type="tel" value={formData.phoneDisplay || ''}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      onFocus={() => setFocusedField('phoneNumber')} onBlur={() => setFocusedField('')}
                      placeholder="XXX XXX XXXX" maxLength="12"
                      style={{ ...getInputStyle('phoneNumber'), paddingLeft: countryCode ? (isMobile ? 60 : 68) : 12 }}
                      autoComplete="off" />
                  </div>
                  {errors.phoneNumber && <div style={errorStyle}><AlertCircle size={11} />{errors.phoneNumber}</div>}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" style={labelStyle}>Message <span style={{ color: "#a4262c", fontWeight: 400 }}>*</span></label>
                  <textarea id="message" name="query" rows={isMobile ? 4 : 5} value={formData.query} onChange={handleChange}
                    onFocus={() => setFocusedField('query')} onBlur={() => setFocusedField('')}
                    placeholder="How can we help you?"
                    style={{ ...getInputStyle('query'), resize: "vertical", minHeight: isMobile ? 90 : 110, paddingTop: 9, fontFamily: SF }} />
                  {errors.query && <div style={errorStyle}><AlertCircle size={11} />{errors.query}</div>}
                </div>

                {/* Submit row */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile ? 12 : 0,
                  paddingTop: 4
                }}>
                  <span style={{ fontSize: isMobile ? 11 : 12, color: subtleColor }}>
                    Fields marked <span style={{ color: "#a4262c" }}>*</span> are required
                  </span>
                  <button
                    id="submit"
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="kc-btn"
                    style={{
                      background: loading ? "#b3b0ad" : accent,
                      width: isMobile ? "100%" : "auto",
                      justifyContent: "center",
                    }}
                  >
                    {loading ? (
                      <>
                        <svg style={{ animation: "spin 0.9s linear infinite", width: 15, height: 15 }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      <><Send size={14} /> Send message</>
                    )}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KrumagoContact;
